import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RecordingHistory = ({ assignments, sessions }) => {
  const [showAll, setShowAll] = useState(false);

  const combinedHistory = [
    ...assignments?.map(a => ({
      type: 'assignment',
      id: a?.id,
      title: a?.lineGroup?.characterName,
      subtitle: a?.lineGroup?.playTitle,
      date: a?.assignedDate,
      details: `${a?.lineGroup?.lineCount || 0} lines recorded`,
      status: a?.status
    })),
    ...sessions?.map(s => ({
      type: 'session',
      id: s?.id,
      title: s?.templateName || 'Recording Session',
      subtitle: s?.location || 'Studio',
      date: s?.scheduledDate,
      details: `${s?.actualStartTime && s?.actualEndTime ? calculateDuration(s?.actualStartTime, s?.actualEndTime) : 'N/A'}`,
      status: s?.status
    }))
  ]?.sort((a, b) => new Date(b.date) - new Date(a.date));

  const displayedHistory = showAll ? combinedHistory : combinedHistory?.slice(0, 5);

  function calculateDuration(start, end) {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationMs = endTime - startTime;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="History" size={24} className="text-primary" />
          Recording History
        </h2>
        <span className="text-sm text-muted-foreground">
          {combinedHistory?.length} total
        </span>
      </div>
      {combinedHistory?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="FileAudio" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No recording history yet</p>
          <p className="text-sm text-muted-foreground mt-2">Complete assignments to build your history</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayedHistory?.map(item => (
              <div 
                key={`${item?.type}-${item?.id}`}
                className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  item?.type === 'assignment' ? 'bg-primary/10' : 'bg-accent/10'
                }`}>
                  <Icon 
                    name={item?.type === 'assignment' ? 'FileText' : 'Mic'} 
                    size={20} 
                    className={item?.type === 'assignment' ? 'text-primary' : 'text-accent'}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">{item?.title}</h3>
                  <p className="text-sm text-muted-foreground">{item?.subtitle}</p>
                  <p className="text-xs text-muted-foreground mt-1">{item?.details}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {new Date(item.date)?.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  <span className="inline-block mt-1 px-2 py-1 rounded text-xs bg-success/10 text-success">
                    Completed
                  </span>
                </div>
              </div>
            ))}
          </div>

          {combinedHistory?.length > 5 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {showAll ? 'Show Less' : `Show All (${combinedHistory?.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default RecordingHistory;